import { Suspense, useEffect, useRef } from "react"; // 1. Import hooks
import { useLocation } from "react-router-dom"; // 2. Import useLocation
import { SidebarContent } from "@/components/portal/layout/Sidebar";
import { BottomNav } from "@/components/portal/layout/BottomNav";
import { Topbar } from "@/components/portal/layout/Topbar";
import { useSidebarStore } from "@/store/useSidebarStore";
import { cn } from "@/lib/utils";
import { PageTransition } from "@/components/common/PageTransition";
import { NavigationProgress } from "@/components/common/NavigationProgress";
import { RouteFallback } from "@/components/common/RouteFallback";
import { WorkspaceBootGate } from "@/components/auth/WorkspaceBootGate";
import { useWorkspaceSwitchReload } from "@/hooks/useWorkspaceSwitchReload";
import { useHelpHotkey } from "@/hooks/useHelpHotkey";
import { BrandingReminderDialog } from "@/components/portal/partials/branding/BrandingReminderDialog";
import { ImpersonationBanner } from "@/components/portal/common/impersonation/ImpersonationBanner";
import { SubscriptionExpiredBanner } from "@/components/portal/common/SubscriptionExpiredBanner";

export const PortalLayout = () => {
  const { isCollapsed, mobileNavMode } = useSidebarStore();
  const { pathname } = useLocation(); // 3. Get current route path
  const mainContentRef = useRef<HTMLDivElement>(null); // 4. Create a ref for the scrollable container

  // Reload tenant-scoped data when a super admin switches workspace.
  useWorkspaceSwitchReload();

  // Hidden "?" shortcut to the Help page while its sidebar item is parked.
  useHelpHotkey();

  // 5. Reset scroll position whenever the route (pathname) changes
  useEffect(() => {
    if (mainContentRef.current) {
      mainContentRef.current.scrollTop = 0;
    }
  }, [pathname]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  return (
    <WorkspaceBootGate>
      <BrandingReminderDialog />
      {/* flex-col so an impersonation banner (when active) sits above the
          sidebar+main row and never overlaps the topbar. The banner renders
          null when not impersonating, so this wrapper costs nothing in the
          normal path. */}
      <div className="h-screen w-full flex flex-col bg-zinc-50 overflow-hidden">
        <ImpersonationBanner />
        <div className="flex flex-1 min-h-0">
          <NavigationProgress />

          {/* Desktop Sidebar (Fixed) — w-60 matches the redesign's 240px rail. */}
          <aside className={cn("hidden lg:block shrink-0 z-20 h-full transition-all duration-300", isCollapsed ? "w-20" : "w-60")}>
            <SidebarContent />
          </aside>

          {/* Main Content Wrapper */}
          <div className="flex-1 flex flex-col h-full min-w-0">
            <Topbar />

            {/* Scrollable Area */}
            <main
              ref={mainContentRef} // 6. Attach the ref here
              className="flex-1 overflow-y-auto p-4 lg:py-6 lg:px-8"
            >
              {/* Inside the scroll area rather than pinned above it: an expired
                  subscription is context, not an alarm, and the learner can
                  scroll past it to the certificates and history that still
                  work. Renders nothing unless the subscription has lapsed. */}
              <SubscriptionExpiredBanner />

              {/* Inner Suspense catches lazy page chunks so the portal shell
                  stays mounted and the root-level PageLoader gradient never
                  flashes during in-portal navigation. */}
              <Suspense fallback={<RouteFallback />}>
                <PageTransition className="w-full" />
              </Suspense>
            </main>

            {/* Mobile bottom navigation — only when the user opts into it.
                Sits below the scrollable main as a flex sibling, so it never
                overlaps content and needs no padding hack. */}
            {mobileNavMode === "bottom" && <BottomNav />}
          </div>
        </div>
      </div>
    </WorkspaceBootGate>
  );
};